import { useState } from "react";
import { message } from "antd";
import { updateAssistant } from "../service";
import { Field, Label, Description } from "V2.0/common/fieldset";
import { Input } from "V2.0/common/input";
import { Switch } from "V2.0/common/switch";
import { Button } from "V2.0/common/button";
import { showErrorToast } from "V2.0/utils";
import SourceAssistants from "./SourceAssistants";

export default PastContextSettings;

function getPastContextSettingsState(pastContextSettings) {
  return {
    enabled: pastContextSettings?.enabled ?? false,
    sourceAssistantIds: pastContextSettings?.sourceAssistantIds ?? [],
    lookbackDays: pastContextSettings?.lookbackDays ?? 30,
    includeArchived: pastContextSettings?.includeArchived ?? true,
  };
}

function PastContextSettings(props) {
  const {
    pastContextSettings,
    workspaceId,
    assistantId,
    syncAssistant,
    assistants,
    locked,
  } = props;

  const [pastContextSettingsState, setPastContextSettingsState] = useState(() =>
    getPastContextSettingsState(pastContextSettings),
  );
  const [loading, setLoading] = useState(false);

  const updatePastContextSettingsState = (field, value) => {
    setPastContextSettingsState((prevState) => {
      return {
        ...prevState,
        [field]: value,
      };
    });
  };

  const handleSave = async () => {
    function init() {
      setLoading(true);
      message.loading("Updating Settings");
    }

    function success() {
      setLoading(false);
      message.destroy();
      message.success("Settings Updated");
      syncAssistant((prevAssistant) => {
        return {
          ...prevAssistant,
          pastContextSettings: pastContextSettingsState,
        };
      });
    }

    function error(e) {
      setLoading(false);
      message.destroy();
      showErrorToast(e, "Failed to Update Settings");
    }

    const payload = {
      assistantSettings: {
        pastContextSettings: pastContextSettingsState,
      },
    };

    try {
      init();

      const response = await updateAssistant({
        projectId: workspaceId,
        assistantId,
        payload: payload,
      });

      success();
    } catch (e) {
      error(e);
    }
  };

  return (
    <div className="flex flex-col gap-y-4 w-3/4 m-4 min-w-[600px] border rounded-lg">
      <div className="px-6 py-4 bg-gray-50 rounded-t-lg">
        <p className="text-md font-semibold text-gray-900">
          Conversation History
        </p>
        <p className="text-sm mt-1 text-gray-500">
          Control whether previous conversations are used to improve responses.
        </p>
      </div>
      <div className="grid grid-cols-4 px-6 gap-y-8 py-4">
        <div className="flex items-center gap-x-2 col-span-4">
          <p className="text-sm font-medium">Use Conversation History</p>
          <Switch
            color="emerald"
            checked={pastContextSettingsState.enabled}
            onChange={() =>
              updatePastContextSettingsState(
                "enabled",
                !pastContextSettingsState.enabled,
              )
            }
            disabled={locked}
          />
        </div>

        <div className="col-span-3 xl:col-span-2">
          <Field>
            <Label>History (Days)</Label>
            <Description>How many days of history to use</Description>
            <Input
              type="number"
              min={1}
              max={365}
              value={pastContextSettingsState.lookbackDays}
              onChange={(e) =>
                updatePastContextSettingsState(
                  "lookbackDays",
                  Number(e.target.value),
                )
              }
              disabled={!pastContextSettingsState.enabled || locked}
            />
          </Field>
        </div>

        <div className="flex flex-col gap-y-1 col-span-4">
          <div className="flex items-center gap-x-2">
            <p className="text-sm font-medium">
              Include Archived Conversations
            </p>

            <Switch
              checked={pastContextSettingsState.includeArchived}
              color="emerald"
              onChange={() =>
                updatePastContextSettingsState(
                  "includeArchived",
                  !pastContextSettingsState.includeArchived,
                )
              }
              disabled={!pastContextSettingsState.enabled || locked}
            />
          </div>
        </div>

        <div className="col-span-4 xl:col-span-3">
          <SourceAssistants
            assistants={assistants}
            selectedAssistant={pastContextSettingsState.sourceAssistantIds}
            updateSelectedAssistants={(assistants) =>
              updatePastContextSettingsState("sourceAssistantIds", assistants)
            }
            disabled={!pastContextSettingsState.enabled || locked}
          />
        </div>
      </div>

      {!locked && (
        <div className="bg-gray-50 px-6 py-3 text-right rounded-b-lg">
          <Button
            onClick={handleSave}
            disabled={loading}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
}
